'use client'

import { m } from 'framer-motion'
import { ArrowUpRight } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'

interface CaseProps {
  slug: string
  title: string
  client: string
  description: string
  year: string | null
  coverImage?: string | null
  tags?: string | null
}

export default function CaseCard({ project, index }: { project: CaseProps; index: number }) {
  const [imageBroken, setImageBroken] = useState(false)
  const showImage = Boolean(project.coverImage) && !imageBroken

  return (
    <m.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="group h-full"
    >
      <Link
        href={`/cases/${project.slug}`}
        data-testid={`case-card-${project.slug}`}
        className="block h-full"
      >
        <div className="relative flex h-full flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(165deg,rgba(255,255,255,0.07)_0%,rgba(255,255,255,0.025)_42%,rgba(255,255,255,0.01)_100%)] backdrop-blur-xl transition-all duration-500 contain-content hover:border-white/25 hover:shadow-2xl hover:shadow-blue-500/20 sm:rounded-[32px]">
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,rgba(255,255,255,0.08),transparent_34%)]" />
          <div className="pointer-events-none absolute -top-24 -right-20 h-56 w-56 bg-blue-500/12 blur-[80px] will-change-transform" />
          <div className="relative aspect-[4/3] overflow-hidden">
            {showImage ? (
              <Image
                src={project.coverImage as string}
                alt={project.title}
                fill
                sizes="(min-width: 1280px) 30vw, (min-width: 768px) 46vw, 92vw"
                className="object-cover transition-transform duration-700 group-hover:scale-105"
                onError={() => setImageBroken(true)}
              />
            ) : (
              <div className="h-full w-full bg-gradient-to-br from-blue-900/40 via-[#0c1220] to-purple-900/40" />
            )}

            <div className="absolute top-4 left-4 z-10 sm:top-6 sm:left-6">
              <span className="home-meta rounded-full border border-white/10 bg-black/50 px-3 py-1 text-gray-300 backdrop-blur-md">
                {project.year || '2025'}
              </span>
            </div>

            <div className="absolute inset-0 flex hidden items-center justify-center bg-black/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100 sm:flex">
              <div className="flex h-16 w-16 scale-50 transform items-center justify-center rounded-full border border-white/20 bg-white/10 backdrop-blur-xl transition-all duration-300 group-hover:scale-100">
                <ArrowUpRight className="h-6 w-6 text-white" />
              </div>
            </div>

            <div className="absolute right-4 bottom-4 z-10 sm:hidden">
              <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/5 backdrop-blur-xl">
                <ArrowUpRight className="h-5 w-5 text-white/90" />
              </div>
            </div>
          </div>

          <div className="flex flex-grow flex-col p-5 sm:p-6 lg:p-8">
            <div className="mb-4">
              <h3 className="home-card-title-sm line-clamp-2 min-h-[4.4rem] leading-[1.08] text-white transition-all group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-gray-400 group-hover:bg-clip-text group-hover:text-transparent">
                {project.title}
              </h3>
              <p className="home-caption mt-2 overflow-hidden text-ellipsis whitespace-nowrap text-gray-400/90">
                {project.client}
              </p>
            </div>

            <p className="home-subnote mb-5 line-clamp-2 min-h-[3.4rem] leading-relaxed text-gray-300/90 sm:mb-6">
              {project.description}
            </p>

            <div className="mt-auto flex min-h-[3.2rem] items-center justify-between gap-3 border-t border-white/10 pt-4 sm:pt-6">
              <div className="flex flex-wrap gap-2">
                {project.tags ? (
                  project.tags
                    .split(',')
                    .slice(0, 2)
                    .map((tag, i) => (
                      <span
                        key={i}
                        className="home-meta rounded-md border border-white/10 bg-white/5 px-2.5 py-1 text-gray-400"
                      >
                        {tag.trim()}
                      </span>
                    ))
                ) : (
                  <span className="home-meta text-gray-500">Премиум-кейс</span>
                )}
              </div>

              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/15 bg-white/5 transition-all duration-300 group-hover:border-white group-hover:bg-white">
                <ArrowUpRight className="h-4 w-4 text-white/90 transition-colors group-hover:text-black" />
              </div>
            </div>
          </div>
        </div>
      </Link>
    </m.div>
  )
}
